import type { ReadProjectType } from '@readme/api/mappings/project/types';
import type { ReactNode } from 'react';

import { useEffect } from 'react';

import useReadmeApi from '@core/hooks/useReadmeApi';

import { projectStore, useProjectStore } from '../Project';
import { useSuperHubStore } from '../SuperHub';

/**
 * Establishes an SWR connection to the v2 API endpoint and continually keeps
 * the project store in sync with the latest data stored in our DB.
 */
export function ConnectProjectStoreToApi({ children }: { children: ReactNode }) {
  const [subdomain, reset, initializePlanFromApiData] = useProjectStore(s => [
    s.data.subdomain,
    s.reset,
    s.plan.initializeFromApiData,
  ]);
  const defaultApiOptions = useSuperHubStore(s => s.defaultApiOptions);

  const apiUrl = `/${subdomain}/api-next/v2/projects/me`;
  const { data: response } = useReadmeApi<ReadProjectType>(apiUrl, {
    ...defaultApiOptions,
    swr: {
      revalidateOnFocus: true,
      shouldRetryOnError: true,
    },
  });

  useEffect(() => {
    if (!response) return;
    initializePlanFromApiData(response.data);
  }, [response, initializePlanFromApiData]);

  useEffect(() => {
    if (!response) return;
    const { data, initialData, isFormDirty } = projectStore.getState();
    if (data !== initialData || isFormDirty) return;
    reset(response.data);
  }, [reset, response]);

  return <>{children}</>;
}
